import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { offers } from '../data/offers';
import { FiArrowUp, FiArrowDown, FiExternalLink } from 'react-icons/fi';

type SortField = 'rating' | 'payoutSpeed' | 'bonusValue' | 'trustScore' | 'minDeposit';
type SortDirection = 'asc' | 'desc';

export const Compare: React.FC = () => {
  const [sortField, setSortField] = useState<SortField>('rating');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const sortedOffers = useMemo(() => {
    return [...offers].sort((a, b) => {
      let aValue: number;
      let bValue: number;

      switch (sortField) {
        case 'rating':
          aValue = a.rating10;
          bValue = b.rating10;
          break;
        case 'payoutSpeed':
          aValue = a.scores.payoutSpeed;
          bValue = b.scores.payoutSpeed;
          break;
        case 'bonusValue':
          aValue = a.scores.bonusValue;
          bValue = b.scores.bonusValue;
          break;
        case 'trustScore':
          aValue = a.scores.trustScore;
          bValue = b.scores.trustScore;
          break;
        case 'minDeposit':
          aValue = a.minDepositGbp;
          bValue = b.minDepositGbp;
          break;
        default:
          return 0;
      }

      if (sortDirection === 'asc') {
        return aValue - bValue;
      }
      return bValue - aValue;
    });
  }, [sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <FiArrowUp /> : <FiArrowDown />;
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Compare UK Casino Offers',
    description: 'Compare bonuses, payout speeds, and ratings across top UK-licensed casinos.',
    url: 'https://yourscasinos.com/compare'
  };

  return (
    <>
      <SEO
        title="Compare UK Casino Offers | Side-by-Side Comparison | Your Casinos"
        description="Compare bonuses, payout speeds, ratings, and minimum deposits across top UK casinos. Find the best deal for your needs."
        canonical="/compare"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-black py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Compare Casino Offers
            </h1>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Side-by-side comparison of bonuses, payout speeds, and key metrics. Click any column header to sort.
            </p>
          </div>

          <div className="glass-card overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-primary-green/30">
                  <th className="px-4 py-4 text-left">
                    <button
                      onClick={() => handleSort('rating')}
                      className="flex items-center gap-2 text-primary-green font-bold hover:text-primary-lightgreen"
                    >
                      Casino
                      <SortIcon field="rating" />
                    </button>
                  </th>
                  <th className="px-4 py-4 text-left">
                    <button
                      onClick={() => handleSort('rating')}
                      className="flex items-center gap-2 text-primary-green font-bold hover:text-primary-lightgreen"
                    >
                      Rating
                      <SortIcon field="rating" />
                    </button>
                  </th>
                  <th className="px-4 py-4 text-left">
                    <button
                      onClick={() => handleSort('bonusValue')}
                      className="flex items-center gap-2 text-primary-green font-bold hover:text-primary-lightgreen"
                    >
                      Bonus
                      <SortIcon field="bonusValue" />
                    </button>
                  </th>
                  <th className="px-4 py-4 text-left">
                    <button
                      onClick={() => handleSort('payoutSpeed')}
                      className="flex items-center gap-2 text-primary-green font-bold hover:text-primary-lightgreen"
                    >
                      Payout Speed
                      <SortIcon field="payoutSpeed" />
                    </button>
                  </th>
                  <th className="px-4 py-4 text-left">
                    <button
                      onClick={() => handleSort('trustScore')}
                      className="flex items-center gap-2 text-primary-green font-bold hover:text-primary-lightgreen"
                    >
                      Trust Score
                      <SortIcon field="trustScore" />
                    </button>
                  </th>
                  <th className="px-4 py-4 text-left">
                    <button
                      onClick={() => handleSort('minDeposit')}
                      className="flex items-center gap-2 text-primary-green font-bold hover:text-primary-lightgreen"
                    >
                      Min Deposit
                      <SortIcon field="minDeposit" />
                    </button>
                  </th>
                  <th className="px-4 py-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedOffers.map((offer, index) => (
                  <tr
                    key={offer.slug}
                    className={`border-b border-primary-green/10 ${
                      index % 2 === 0 ? 'bg-black/20' : ''
                    }`}
                  >
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={offer.logo}
                          alt={offer.name}
                          className="h-auto w-12"
                          loading="lazy"
                        />
                        <span className="font-medium text-white">{offer.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-2xl font-bold text-primary-green">
                        {offer.rating10}/10
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-white font-medium">{offer.bonusHeadline}</div>
                      <div className="text-sm text-gray-400">Score: {offer.scores.bonusValue}/100</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-white font-medium">{offer.payoutHeadline}</div>
                      <div className="text-sm text-gray-400">Score: {offer.scores.payoutSpeed}/100</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-white font-bold">{offer.scores.trustScore}/100</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-white font-medium">£{offer.minDepositGbp}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex gap-2 justify-center">
                        <Link
                          to={`/offers/${offer.slug}`}
                          className="btn-primary text-xs py-2 px-3"
                        >
                          Review
                        </Link>
                        <a
                          href={offer.officialUrl}
                          target="_blank"
                          rel="noreferrer noopener sponsored"
                          className="btn-secondary text-xs py-2 px-3 inline-flex items-center"
                        >
                          Site
                          <FiExternalLink className="ml-1" size={12} />
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

